import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type {
  AdminTenantListItem,
  AdminTenantsSortBy,
  AdminTenantsSortOrder,
} from "../../../services/tenantDeletionApi";
import { CLIENT_SOURCE_LABELS } from "../../../services/tenantDeletionApi";
import { ModuleDisplay } from "../../../components/tenants/ModuleDisplay";
import TenantMigrationModal from "../billing/components/TenantMigrationModal";

const formatDate = (iso: string | null): string => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Formats a migratedAt ISO string using Intl.DateTimeFormat per spec A2.
 */
const formatMigratedAt = (iso: string): string => {
  return new Intl.DateTimeFormat("pl-PL").format(new Date(iso));
};

// Re-export for convenience (existing consumers in AdminTenants.tsx import these).
export type { AdminTenantsSortBy };
export type SortOrder = AdminTenantsSortOrder;

export interface AdminTenantsTableProps {
  items: AdminTenantListItem[];
  sortBy: AdminTenantsSortBy;
  sortOrder: SortOrder;
  onSort: (column: AdminTenantsSortBy) => void;
  onDelete: (tenant: AdminTenantListItem) => void;
}

/**
 * Table sub-component for AdminTenants page.
 * Preserves accessibility (scope="col", aria-sort) added by the HIGH wave fix.
 * Adds "Migracja" column (A2-A3) with Migruj button or migratedAt badge.
 */
export const AdminTenantsTable: React.FC<AdminTenantsTableProps> = ({
  items,
  sortBy,
  sortOrder,
  onSort,
  onDelete,
}) => {
  const queryClient = useQueryClient();
  const [migratingTenant, setMigratingTenant] = useState<AdminTenantListItem | null>(null);

  const ariaSort = (col: AdminTenantsSortBy): "ascending" | "descending" | "none" => {
    if (sortBy !== col) return "none";
    return sortOrder === "desc" ? "descending" : "ascending";
  };

  const handleSortKeyDown = (
    e: React.KeyboardEvent<HTMLTableCellElement>,
    col: AdminTenantsSortBy
  ): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSort(col);
    }
  };

  const crossTenantNote =
    "Ostatnie logowanie któregokolwiek członka organizacji (do dowolnej organizacji, do której ma dostęp)";

  return (
    <>
      <div className="overflow-x-auto bg-white rounded-md border border-neutral-200">
        <table
          className="min-w-full divide-y divide-neutral-200"
          data-testid="admin-tenants-table"
        >
          <thead className="bg-neutral-50">
            <tr>
              <th
                scope="col"
                onClick={() => onSort("name")}
                onKeyDown={(e) => handleSortKeyDown(e, "name")}
                tabIndex={0}
                aria-sort={ariaSort("name")}
                className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                data-testid="admin-tenants-sort-name"
              >
                Nazwa{" "}
                {sortBy === "name" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
              >
                NIP
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
              >
                Telefon
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
              >
                Moduły
              </th>
              {/* A2: Migracja column — between Moduły and Utworzono */}
              <th
                scope="col"
                className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
              >
                Migracja
              </th>
              <th
                scope="col"
                onClick={() => onSort("createdAt")}
                onKeyDown={(e) => handleSortKeyDown(e, "createdAt")}
                tabIndex={0}
                aria-sort={ariaSort("createdAt")}
                className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                data-testid="admin-tenants-sort-createdAt"
              >
                Utworzono{" "}
                {sortBy === "createdAt" && (sortOrder === "asc" ? "▲" : "▼")}
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider"
              >
                Użytkownicy
              </th>
              <th
                scope="col"
                onClick={() => onSort("lastActivityAt")}
                onKeyDown={(e) => handleSortKeyDown(e, "lastActivityAt")}
                tabIndex={0}
                aria-sort={ariaSort("lastActivityAt")}
                aria-describedby="tenant-list-cross-tenant-note"
                className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                data-testid="admin-tenants-sort-lastActivityAt"
                title={crossTenantNote}
              >
                Ostatnia aktywność{" "}
                {sortBy === "lastActivityAt" && (sortOrder === "asc" ? "▲" : "▼")}
                <span
                  className="ml-1 text-gray-400"
                  aria-hidden="true"
                  title={crossTenantNote}
                >
                  (i)
                </span>
                <span id="tenant-list-cross-tenant-note" className="sr-only">
                  {crossTenantNote}
                </span>
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
              >
                Źródło
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider"
              >
                Akcje
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-200">
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-6 text-center text-neutral-500"
                  data-testid="admin-tenants-empty"
                >
                  Brak tenantów pasujących do kryteriów.
                </td>
              </tr>
            )}
            {items.map((tenant) => (
              <tr
                key={tenant.id}
                data-testid={`admin-tenants-row-${tenant.id}`}
              >
                <td className="px-4 py-2 text-sm font-medium">
                  <Link
                    to={`/admin/tenants/${tenant.id}`}
                    className="text-primary-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                  >
                    {tenant.name}
                  </Link>
                </td>
                <td className="px-4 py-2 text-sm text-neutral-600">
                  {tenant.nip || "-"}
                </td>
                <td className="px-4 py-2 text-sm text-neutral-600">
                  {tenant.phone || "-"}
                </td>
                <td className="px-4 py-2 text-sm text-neutral-600">
                  <ModuleDisplay enabledModules={tenant.enabledModules ?? []} />
                </td>
                {/* A2: Migration cell */}
                <td className="px-4 py-2 text-sm text-neutral-600">
                  {tenant.migratedAt != null ? (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                      data-testid={`admin-tenants-migrated-badge-${tenant.id}`}
                    >
                      {formatMigratedAt(tenant.migratedAt)}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setMigratingTenant(tenant)}
                      aria-label={`Migruj tenant ${tenant.name}`}
                      data-testid={`admin-tenants-migrate-btn-${tenant.id}`}
                      className="text-xs font-medium text-amber-600 hover:text-amber-900 transition-colors"
                    >
                      Migruj
                    </button>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-neutral-600">
                  {formatDate(tenant.createdAt)}
                </td>
                <td className="px-4 py-2 text-sm text-neutral-600 text-right">
                  {tenant.usersCount === 0 ? "—" : tenant.usersCount}
                </td>
                <td className="px-4 py-2 text-sm text-neutral-600">
                  {formatDate(tenant.lastActivityAt)}
                </td>
                <td className="px-4 py-2 text-sm text-neutral-600">
                  {tenant.clientSource != null
                    ? CLIENT_SOURCE_LABELS[tenant.clientSource]
                    : CLIENT_SOURCE_LABELS.none}
                </td>
                <td className="px-4 py-2 text-sm text-right">
                  <button
                    type="button"
                    onClick={() => onDelete(tenant)}
                    data-testid={`admin-tenants-delete-${tenant.id}`}
                    className="rounded-md bg-red-600 text-white px-3 py-1 text-xs font-medium hover:bg-red-700"
                  >
                    Usuń
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* A3: TenantMigrationModal — opened on "Migruj" click */}
      {migratingTenant && (
        <TenantMigrationModal
          tenantId={migratingTenant.id}
          tenantName={migratingTenant.name}
          onClose={() => setMigratingTenant(null)}
          onMigrationComplete={() => {
            // A3: Invalidate admin tenants list to refresh migratedAt badge
            // Note: modal stays open so user sees Result state; closed via "Zamknij" button
            queryClient.invalidateQueries({ queryKey: ["adminTenants"] });
          }}
        />
      )}
    </>
  );
};

export default AdminTenantsTable;
